import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { updateTag } from 'api/tags';
import { queryKeys } from 'constants/queryKeys';
import { tagManagementStrings } from 'constants/strings';
import { useAuth } from 'contexts/AuthContext';
import type { TTag, TUpsertTagRequest } from 'types';
import { toErrorMessage } from './utils';

type UseUpdateTagOptions = {
  onSuccess?: (tag: TTag) => void;
};

type UseUpdateTagResult = {
  update: (tagId: number, request: TUpsertTagRequest) => void;
  isSubmitting: boolean;
  error: string | undefined;
};

export function useUpdateTag(
  options: UseUpdateTagOptions = {},
): UseUpdateTagResult {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const strings = tagManagementStrings.edit;

  const [error, setError] = useState<string | undefined>();

  const mutation = useMutation({
    mutationFn: async (params: {
      tagId: number;
      request: TUpsertTagRequest;
    }) => {
      if (!user) {
        throw new Error(strings.errors.unauthorized);
      }
      return updateTag(params.tagId, params.request);
    },
    onSuccess: (tag) => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.TAGS, user?.uid],
      });
      // タグ名・色の変更を友だち一覧/詳細のチップにも反映させる。
      queryClient.invalidateQueries({
        queryKey: [queryKeys.LINE_FRIENDS, user?.uid],
      });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.LINE_FRIEND_DETAIL],
      });
      setError(undefined);
      options.onSuccess?.(tag);
    },
    onError: (caught) => {
      setError(toErrorMessage(caught, strings.errors.submitFailed));
    },
  });

  const update = (tagId: number, request: TUpsertTagRequest) => {
    setError(undefined);
    mutation.mutate({ tagId, request });
  };

  return {
    update,
    isSubmitting: mutation.isPending,
    error,
  };
}
